import { Fragment, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconDownload, IconHistory, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';

interface VersionEntry {
  id: number;
  version_label: string | null;
  status: string;
  effective_date: string | null;
  superseded_at: string | null;
  original_name: string | null;
}

interface LibraryDocumentRow {
  id: number;
  category: string;
  title: string;
  version_label: string | null;
  status: 'draft' | 'published' | 'archived';
  effective_date: string | null;
  published_by_name: string | null;
  original_name: string | null;
  created_at: string | null;
  previous_versions: VersionEntry[];
}

const emptyForm = { category: '', title: '', version_label: '', effective_date: '', draft: false };

// "+ Add Document" leaves previousVersionId null; a per-row "Upload New
// Version" sets it and locks category/title — the row the user clicked is
// what it supersedes, never a manual pick (see LibraryDocumentController::store()).
export function Library() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [documents, setDocuments] = useState<LibraryDocumentRow[] | null>(null);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ search: '', category: '' });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [uploading, setUploading] = useState(false);
  const [previousVersionId, setPreviousVersionId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canPublish = can('library.publish');
  const canArchive = can('library.archive');

  const query = useMemo(
    () => ({ search: filters.search || undefined, category: filters.category || undefined }),
    [filters]
  );

  const load = () => {
    api
      .get<LibraryDocumentRow[]>('/library-documents', { params: query })
      .then((res) => setDocuments(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the library.'));
  };

  useEffect(load, [query]);

  const openAdd = () => {
    setPreviousVersionId(null);
    setForm(emptyForm);
    setFile(null);
    setErrors({});
    setUploading(true);
  };

  const openNewVersion = (row: LibraryDocumentRow) => {
    setPreviousVersionId(row.id);
    setForm({ category: row.category, title: row.title, version_label: '', effective_date: '', draft: false });
    setFile(null);
    setErrors({});
    setUploading(true);
  };

  const submitUpload = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const data = new FormData();
    data.append('category', form.category);
    data.append('title', form.title);
    if (form.version_label) data.append('version_label', form.version_label);
    if (form.effective_date) data.append('effective_date', form.effective_date);
    data.append('status', form.draft ? 'draft' : 'published');
    if (previousVersionId) data.append('previous_version_id', String(previousVersionId));
    data.append('file', file);
    try {
      await api.post('/library-documents', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploading(false);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const publish = async (row: LibraryDocumentRow) => {
    try {
      await api.post(`/library-documents/${row.id}/publish`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Publish failed.');
    }
  };

  const archive = async (row: LibraryDocumentRow) => {
    if (!(await confirmAction(`Archive "${row.title}"? It will stay downloadable but no longer show as current.`))) return;
    try {
      await api.post(`/library-documents/${row.id}/archive`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Archive failed.');
    }
  };

  const removeDraft = async (row: LibraryDocumentRow) => {
    if (!(await confirmAction(`Delete the draft "${row.title}"?`))) return;
    try {
      await api.delete(`/library-documents/${row.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const download = (id: number, name: string | null) => downloadFile(api, `/library-documents/${id}/download`, name ?? 'document');

  return (
    <>
      <header className="page-header">
        <h1>Library</h1>
        {canPublish && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add Document
          </button>
        )}
      </header>
      <p className="muted">Institutional policies, procedures, and the org chart — available to every staff member.</p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by title…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All categories</option>
          {listOptions(lists, 'library_document_category').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Version</th>
                <th>Effective Date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!documents && !error ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : documents && documents.length > 0 ? (
                documents.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      <td>
                        <strong>{row.title}</strong>
                      </td>
                      <td className="muted">{labelFor(lists, 'library_document_category', row.category)}</td>
                      <td className="muted">{row.version_label ?? '—'}</td>
                      <td className="muted">{row.effective_date ?? '—'}</td>
                      <td>
                        <span className={`badge badge-${row.status === 'published' ? 'active' : row.status === 'draft' ? 'scheduled' : 'inactive'}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="right">
                        <IconButton icon={IconDownload} label={row.original_name ?? 'Download'} onClick={() => download(row.id, row.original_name)} />
                        {row.previous_versions.length > 0 && (
                          <IconButton
                            icon={IconHistory}
                            label="Version history"
                            onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          />
                        )}
                        {canPublish && row.status === 'draft' && (
                          <IconButton icon={IconCheck} label="Publish" onClick={() => publish(row)} />
                        )}
                        {canPublish && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => openNewVersion(row)}>
                            Upload New Version
                          </button>
                        )}
                        {canArchive && row.status === 'published' && (
                          <IconButton icon={IconTrash} label="Archive" variant="danger-ghost" onClick={() => archive(row)} />
                        )}
                        {canArchive && row.status === 'draft' && (
                          <IconButton icon={IconTrash} label="Delete draft" variant="danger-ghost" onClick={() => removeDraft(row)} />
                        )}
                      </td>
                    </tr>
                    {expandedId === row.id && row.previous_versions.length > 0 && (
                      <tr>
                        <td colSpan={6}>
                          <table>
                            <thead>
                              <tr>
                                <th>Version</th>
                                <th>Effective Date</th>
                                <th>Superseded</th>
                                <th>File</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.previous_versions.map((v) => (
                                <tr key={v.id}>
                                  <td className="muted">{v.version_label ?? '—'}</td>
                                  <td className="muted">{v.effective_date ?? '—'}</td>
                                  <td className="muted">{v.superseded_at ?? '—'}</td>
                                  <td className="muted">{v.original_name ?? '—'}</td>
                                  <td className="right">
                                    <IconButton icon={IconDownload} label="Download" onClick={() => download(v.id, v.original_name)} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No documents yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {uploading && (
        <Modal title={previousVersionId ? `Upload new version — ${form.title}` : 'Add Document'} onClose={() => setUploading(false)}>
          <form onSubmit={submitUpload} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Category *
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                required
                disabled={!!previousVersionId}
              >
                <option value="">Select…</option>
                {listOptions(lists, 'library_document_category').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.category && <small className="error">{errors.category[0]}</small>}
            </label>
            <label className="span-2">
              Title *
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                maxLength={255}
                disabled={!!previousVersionId}
              />
              {errors.title && <small className="error">{errors.title[0]}</small>}
            </label>
            <label>
              Version label
              <input
                value={form.version_label}
                onChange={(e) => setForm({ ...form, version_label: e.target.value })}
                placeholder="e.g. v2"
                maxLength={50}
              />
              {errors.version_label && <small className="error">{errors.version_label[0]}</small>}
            </label>
            <label>
              Effective date
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
              />
              {errors.effective_date && <small className="error">{errors.effective_date[0]}</small>}
            </label>
            <label className="span-2">
              File *
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
                required
              />
              {errors.file && <small className="error">{errors.file[0]}</small>}
            </label>
            <label className="checkbox-inline span-2">
              <input type="checkbox" checked={form.draft} onChange={(e) => setForm({ ...form, draft: e.target.checked })} />
              Save as draft (don't publish yet)
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setUploading(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.category || !form.title || !file}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
